import React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { BaseSettingsTable } from "../components/BaseSettingsTable";
import { BaseSettingsCell } from "../components/BaseSettingsCell";
import { Badge } from "@/components/ui/badge";
import { UsersSettingsTab, UsersSettingsTabs } from "./tabs";
import { UserDetailsDialog } from "./UserDetailsDialog";
import { useT } from "@/i18n";

export type UserRowModel = { id: string } & Partial<{
  email: string;
  name: string;
  roles: string[];
  branchesCount: number;
  businessesCount: number;
  is_root: boolean;
}>;

export type SettingsUsersTableProps = {
  accounts: UserRowModel[];
  selectedTab: UsersSettingsTab;
  businessId?: string;
};

export function SettingsUsersTable({ accounts, selectedTab, businessId }: SettingsUsersTableProps) {
  const [selectedUser, setSelectedUser] = React.useState<UserRowModel | null>(null);
  const [showInviteDialog, setShowInviteDialog] = React.useState(false);
  const { t, formatNumber } = useT();

  const handleRowClick = (user: UserRowModel) => {
    setSelectedUser(user);
    setShowInviteDialog(true);
  };

  const handleCloseDialog = () => {
    setShowInviteDialog(false);
    setSelectedUser(null);
  };

  const columns: ColumnDef<UserRowModel, keyof UserRowModel>[] = [
    {
      accessorKey: "name",
      header: t("common.name"),
      cell: ({ getValue, row }) => {
        const name = getValue() as string;
        const email = row.original.email as string;
        return BaseSettingsCell.avatar(name || email);
      },
    },
    {
      accessorKey: "email",
      header: t("common.email"),
      cell: ({ getValue }) => (
        <span dir="auto" className="text-muted-foreground">
          {getValue() as string}
        </span>
      ),
    },
  ];

  if (selectedTab === UsersSettingsTabs.active) {
    columns.push({
      accessorKey: "is_root",
      header: t("users.biRole"),
      cell: ({ getValue }) => (
        <Badge variant={getValue() ? "secondary" : "outline"}>
          {getValue() ? t("users.admin") : t("users.normal")}
        </Badge>
      ),
    });
  } else {
    columns.push(
      {
        accessorKey: "roles",
        header: t("users.foodicsRoles"),
        cell: ({ row }) =>
          row.original.roles?.length ? (
            <span dir="auto">{row.original.roles.join(", ")}</span>
          ) : (
            t("users.noRoles")
          ),
      },
      {
        accessorKey: "branchesCount",
        header: t("users.branches"),
        cell: ({ row }) => (
          <span className="tabular-nums">{formatNumber(row.original.branchesCount ?? 0)}</span>
        ),
      },
    );
  }

  return (
    <>
      <BaseSettingsTable data={accounts} columns={columns} onRowClick={handleRowClick} />

      {selectedUser && (
        <UserDetailsDialog
          show={showInviteDialog}
          user={selectedUser}
          selectedTab={selectedTab}
          businessId={businessId}
          onCancel={handleCloseDialog}
        />
      )}
    </>
  );
}
